import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/kit";
import { useModules, ICONS, iconOf, invalidateModules } from "@/hooks/useModules";
import api, { apiError } from "@/lib/api";

const COLORS = ["#6161FF", "#0074FD", "#00CA72", "#FF3D57", "#FFCC00", "#14B8A6", "#F59E0B", "#A855F7", "#EC4899", "#64748B"];
const EMPTY = { name: "", desc: "", color: "#14B8A6", icon: "Layers" };

const ModuleForm = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ name: initial?.name || "", desc: initial?.desc || "", color: initial?.color || "#14B8A6", icon: initial?.icon || "Layers" });
  const [busy, setBusy] = useState(false);
  const Icon = iconOf(f.icon);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) await api.put(`/modules/${initial.slug}`, f); else await api.post("/modules", f);
      toast.success(initial ? "Module updated" : `Module "${f.name}" created`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="mk-card max-w-lg border-thborder/12 bg-thsolid" data-testid="module-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl font-extrabold text-thstrong">{initial ? "Edit module" : "New module"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="mk-icon-tile h-12 w-12 rounded-xl" style={{ color: f.color, background: `${f.color}1f` }}><Icon className="h-5 w-5" /></span>
            <div className="flex-1">
              <label className="mk-sub">Name</label>
              <input data-testid="module-form-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mk-input mt-1" placeholder="e.g. Training" />
              {!initial && f.name && <p className="mk-sub mt-1 normal-case tracking-normal">URL: /module/{f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}</p>}
            </div>
          </div>
          <div>
            <label className="mk-sub">Description (shown on the hub tile)</label>
            <textarea data-testid="module-form-desc" value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} rows={2} className="mk-input mt-1" placeholder="One line on what this module tracks" />
          </div>
          <div>
            <label className="mk-sub">Accent colour</label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {COLORS.map((c) => (
                <button type="button" key={c} data-testid={`module-color-${c.slice(1)}`} onClick={() => setF({ ...f, color: c })}
                  className={`h-7 w-7 rounded-full transition-transform ${f.color === c ? "scale-110 ring-2 ring-offset-2 ring-offset-thsolid" : ""}`} style={{ background: c, "--tw-ring-color": c }} />
              ))}
              <input type="color" data-testid="module-form-color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-7 w-10 cursor-pointer rounded border-0 bg-transparent" />
            </div>
          </div>
          <div>
            <label className="mk-sub">Icon</label>
            <div className="mt-2 grid grid-cols-8 gap-1.5" data-testid="module-icon-grid">
              {Object.entries(ICONS).map(([n, I]) => (
                <button type="button" key={n} title={n} data-testid={`module-icon-${n}`} onClick={() => setF({ ...f, icon: n })}
                  className={`flex h-9 items-center justify-center rounded-lg border transition-colors ${f.icon === n ? "border-thcyan/60 bg-thcyan/10 text-thcyan" : "border-thborder/10 text-thmute hover:text-thstrong"}`}>
                  <I className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
          <button data-testid="module-form-submit" disabled={busy || !f.name.trim()} className="mk-btn-primary w-full justify-center !py-3">{busy ? "Saving…" : initial ? "Save changes" : "Create module"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ModulesPanel = () => {
  const { modules, reload } = useModules();
  const [form, setForm] = useState(null);

  const saved = () => { setForm(null); invalidateModules(); reload(); };

  const remove = async (m) => {
    if (!window.confirm(`Delete module "${m.name}"? Any uploaded data and permissions for it will be removed.`)) return;
    try {
      const { data } = await api.delete(`/modules/${m.slug}`);
      toast.success(`Deleted ${m.name}${data.uploads_removed ? ` · ${data.uploads_removed} upload(s) removed` : ""}`);
      saved();
    } catch (e) { toast.error(apiError(e)); }
  };

  const move = async (idx, dir) => {
    const list = [...modules];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]];
    try {
      await Promise.all(list.map((m, i) => api.put(`/modules/${m.slug}`, { name: m.name, desc: m.desc, color: m.color, icon: m.icon, order: i })));
      saved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-thstrong">Modules</h2>
          <p className="mt-1 max-w-2xl text-sm text-thmute">Add, edit, reorder or remove the modules shown on the hub and sidebar. Built-in modules have bespoke dashboards and cannot be deleted; custom modules open a data explorer over their uploads.</p>
        </div>
        <button data-testid="module-add-btn" onClick={() => setForm(EMPTY)} className="mk-btn-primary"><Plus className="h-4 w-4" /> Add module</button>
      </div>
      <div className="space-y-2" data-testid="modules-list">
        {(modules || []).map((m, i) => {
          const Icon = iconOf(m.icon);
          return (
            <div key={m.slug} data-testid={`module-row-${m.slug}`} className="mk-card mk-fade flex items-center gap-4 p-4" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex flex-col text-thfaint">
                <button data-testid={`module-up-${m.slug}`} onClick={() => move(i, -1)} disabled={i === 0} className="hover:text-thcyan disabled:opacity-20"><ArrowUp className="h-3.5 w-3.5" /></button>
                <GripVertical className="h-3.5 w-3.5 opacity-40" />
                <button data-testid={`module-down-${m.slug}`} onClick={() => move(i, 1)} disabled={i === modules.length - 1} className="hover:text-thcyan disabled:opacity-20"><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
              <span className="mk-icon-tile h-11 w-11 rounded-xl" style={{ color: m.color, background: `${m.color}1f` }}><Icon className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-heading text-base font-bold text-thstrong">{m.name}</p>
                  {m.builtin ? <Badge tone="info"><Lock className="h-2.5 w-2.5" /> Built-in dashboard</Badge> : <Badge tone="warn">Custom · data explorer</Badge>}
                </div>
                <p className="mt-0.5 truncate text-xs text-thmute">{m.desc || "—"}</p>
                <p className="mk-sub mt-1 normal-case tracking-normal">/module/{m.slug}</p>
              </div>
              <button data-testid={`module-edit-${m.slug}`} onClick={() => setForm(m)} className="rounded-full border border-thborder/12 p-2 text-thmute transition-colors hover:border-thcyan/50 hover:text-thcyan"><Pencil className="h-3.5 w-3.5" /></button>
              {!m.builtin && (
                <button data-testid={`module-delete-${m.slug}`} onClick={() => remove(m)} className="rounded-full border border-thborder/12 p-2 text-thmute transition-colors hover:border-thcoral/40 hover:text-thcoral"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          );
        })}
      </div>
      {form && <ModuleForm initial={form.slug ? form : null} onClose={() => setForm(null)} onSaved={saved} />}
    </div>
  );
};
